import { useAlert } from '@components/alert/alert';
import { getAddToCartError } from '@crocodille/bbdomu-i18n';
import {
  v2_useBranches,
  v2_useCreateNewCart,
  v2_useProductList,
  v2_getProductTitles,
} from '@crocodille/common';
import { getDayOfWeek, isSome, Maybe, memoize, noop } from '@crocodille/util';
import { useTranslation } from 'next-i18next';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { analyticsEvent } from '../../../modules/analytics';
import * as routing from '../../../routing';
import AddressSearch, {
  AddressSearchResult,
} from '../../address-search/address-search';
import BranchMap from '../../branch-map/branch-map';
import Error from '../../elements/error/error';
import BranchCarousel from '../branch-carousel/branch-carousel';

import styles from './shipping-in-restaurant-form.module.scss';

export interface ShippingInRestaurantFormProps {
  navigateToProductList?(): void;
}

const ShippingInRestaurantForm: React.FC<
  React.PropsWithChildren<ShippingInRestaurantFormProps>
> = ({ navigateToProductList = noop }) => {
  const { t } = useTranslation();
  const dispatchAlert = useAlert();

  const dayOfWeek = getDayOfWeek(new Date());

  const [highlightBranch, setHighlightBranch] = useState(null as Maybe<string>);
  const { data: stableBranches } = v2_useBranches();

  const [suggestion, setSuggestion] =
    useState<Maybe<AddressSearchResult>>(null);

  const location = useMemo(() => {
    return suggestion && 'location' in suggestion
      ? {
          longitude: suggestion.location[0],
          latitude: suggestion.location[1],
        }
      : null;
  }, [suggestion]);

  const { data: branches } = v2_useBranches(location, 'approx');
  const { data: updatedBranches, isLoading: updatedBranchesLoading } =
    v2_useBranches(location, 'high');
  const displayedBranches =
    (updatedBranchesLoading ? branches : updatedBranches) ??
    stableBranches ??
    [];

  const { data: products } = v2_useProductList();

  const {
    trigger: createNewCart,
    pendingCart,
    error: newCartError,
  } = v2_useCreateNewCart(
    (cart) => {
      analyticsEvent('add_shipping_info', {
        ecommerce: {
          shipping_tier: `${cart.shippingMethodName} - ${cart.branchCode}`,
        },
      });

      navigateToProductList();
    },
    {
      onError: (e) => {
        if (e && typeof e === 'object') {
          if ('kind' in e && typeof e.kind === 'string') {
            dispatchAlert(
              t(getAddToCartError(e.kind), {
                products:
                  e.kind === 'product_unavailable' && products
                    ? ` (${v2_getProductTitles(
                        products,
                        (e as unknown as { productVariantCodes: string[] })
                          .productVariantCodes,
                      ).join(', ')})`
                    : null,
              }),
              t(getAddToCartError('other')),
            );
          }
        }
      },
    },
  );

  const handleShippingMethodPicked = useCallback(
    async (branchCode: string, shippingMethodId: number) => {
      await createNewCart({
        branchCode,
        delivery: {
          deliveryMethod: 'atBranch',
          shippingMethodId,
        },
      });
    },
    [createNewCart],
  );

  const handleDetailOpen = useMemo(
    () =>
      memoize((branchCode: string) => () => {
        setHighlightBranch((highlightBranch) => {
          if (highlightBranch === branchCode) {
            return null;
          } else {
            return branchCode;
          }
        });
      }),
    [setHighlightBranch],
  );

  const handleBranchClicked = useCallback((branchCode: string) => {
    setHighlightBranch((highlightBranch) => {
      if (highlightBranch === branchCode) {
        return null;
      } else {
        return branchCode;
      }
    });
  }, []);

  return (
    <form className={styles.form}>
      <div className={styles.branches}>
        <AddressSearch
          className={styles.search}
          pickedAddress={suggestion}
          onAddressPicked={setSuggestion}
          queryGeolocation
          autoSelectGeolocation
          suggestSaved
        />
        <BranchCarousel>
          {displayedBranches.map((branch) => (
            <BranchCarousel.Item
              key={branch.code}
              code={branch.code}
              label={branch.name}
              distance={branch.distance}
              address={branch.address}
              availability={branch.availability}
              openFrom={branch.openingHours[dayOfWeek]?.openFrom}
              openTo={branch.openingHours[dayOfWeek]?.openTo}
              openingHours={branch.openingHours}
              email={branch.email}
              phoneNumber={branch.phoneNumber}
              deliveryFrom={
                branch.supportedShippingMethodTypes.includes('delivery')
                  ? branch.openingHours[dayOfWeek]?.deliveryFrom
                  : null
              }
              deliveryTo={
                branch.supportedShippingMethodTypes.includes('delivery')
                  ? branch.openingHours[dayOfWeek]?.deliveryTo
                  : null
              }
              detailHref={routing.branch({ code: branch.code })}
              detailOpen={highlightBranch === branch.code}
              onDetailOpen={handleDetailOpen(branch.code)}
              onShippingMethodPicked={handleShippingMethodPicked}
              shippingMethods={branch.inRestaurantShippingMethods.map(
                (shippingMethod) => ({
                  id: shippingMethod.id,
                  label: shippingMethod.name,
                  disabled:
                    (isSome(pendingCart) &&
                      pendingCart.delivery.deliveryMethod === 'atBranch' &&
                      pendingCart.delivery.shippingMethodId ===
                        shippingMethod.id) ||
                    shippingMethod.disabled,
                }),
              )}
              properties={branch.properties}
            />
          ))}
        </BranchCarousel>
        {isSome(newCartError) ? (
          <Error>{(newCartError as string | Error).toString?.()}</Error>
        ) : null}
      </div>
      <div className={styles.map}>
        <Suspense fallback={null}>
          <BranchMap
            className={styles.mapInner}
            highlightBranch={highlightBranch}
            onBranchClicked={handleBranchClicked}
            branchPoints={stableBranches}
            userLocation={
              suggestion && 'id' in suggestion ? suggestion?.location : null
            }
          />
        </Suspense>
      </div>
    </form>
  );
};

export default ShippingInRestaurantForm;
